import { redirect } from "@tanstack/react-router"
import { Permission, UserRole, hasHigherOrEqualRole, hasPermission, resolveUserRole } from "@/types/permissions"
import { clearAuthState, getTokenClaims, isAuthenticated } from "@/utils/auth"

// Helper to create route guards
export function createRouteGuard({
  requiredRole,
  requiredPermission,
  redirectTo = "/unauthorized"
}: {
  requiredRole?: UserRole
  requiredPermission?: Permission
  redirectTo?: string
}) {
  return async () => {
    // Check if a non-expired token is present.
    if (!isAuthenticated()) {
      throw redirect({ to: "/login" })
    }

    try {
      const tokenClaims = getTokenClaims()
      const userRole = resolveUserRole({ roles: tokenClaims?.roles })

      // Check role requirement
      if (requiredRole && !hasHigherOrEqualRole(userRole, requiredRole)) {
        throw redirect({ to: redirectTo })
      }

      // Check permission requirement
      if (requiredPermission && !hasPermission(userRole, requiredPermission)) {
        throw redirect({ to: redirectTo })
      }

      return { tokenClaims }
    } catch (error) {
      // Check if this is a TanStack Router redirect
      if (error instanceof Response && (error as any).options?.to) {
        throw error // Re-throw redirect
      }
      clearAuthState()
      throw redirect({ to: "/login" })
    }
  }
}

// Specific guards for common use cases
export const requireSuperUser = () => createRouteGuard({ 
  requiredRole: UserRole.SUPER_USER 
})

export const requireProviderAdmin = () => createRouteGuard({ 
  requiredRole: UserRole.PROVIDER_ADMIN 
})

export const requireProviderGroupAdmin = () => createRouteGuard({ 
  requiredRole: UserRole.PROVIDER_GROUP_ADMIN 
})

export const requireAdminAccess = () => createRouteGuard({ 
  requiredPermission: Permission.VIEW_ADMIN_PANEL 
})